import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { useMemoDeepEqualValue } from '@plato/ui-legacy/lib/hooks/use-memo-deep-equal';
import { useQueryParams } from '@plato/ui-legacy/lib/hooks/use-query-params';
import { useReferrer } from '@plato/ui-legacy/lib/hooks/use-referrer';
import { enumParam, numberParam, queryParamsConfig, stringParam } from '@plato/ui-legacy/lib/query-params';
import { Dialog, DialogContent, DialogTitle } from '@plato/ui/containers/dialog';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InsightOutcomeDetails } from '../insights-common/components/insight-outcome-details.tsx';
import InventoryDetails from '../inventory/components/inventory-details.tsx';
import { QuoteOrderDetailPage } from '../quote-order-detail/pages/quote-order-detail-page.tsx';
import { TaskDetails } from '../tasks/components/task-details.tsx';
import type { DialogsContextType } from './dialogs-context.ts';
import { DialogsContext } from './dialogs-context.ts';

const dialogQueryParams = queryParamsConfig({
    d_depth: numberParam(),
    d_product_variant_id: stringParam(),
    d_transaction_id: stringParam(),
    d_transaction_type: enumParam(SrSalesOrderType),
    d_customer_id: stringParam(),
    d_task_id: stringParam(),
    d_insight_outcome_id: stringParam(),
});

export function AllDialogsProvider({ children }: { children?: ReactNode }) {
    const [, setQueryParams] = useQueryParams(dialogQueryParams);

    const openProductDetails = useCallback(
        (nextProductId: string, customerId?: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_customer_id: customerId,
                    d_product_variant_id: nextProductId,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openOrderQuoteDetails = useCallback(
        (type: SrSalesOrderType, id: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_transaction_type: type,
                    d_transaction_id: id,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openTaskDetails = useCallback(
        (taskId: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_task_id: taskId,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const openInsightOutcomeDetails = useCallback(
        (outcomeId: string) => {
            setQueryParams(
                ({ d_depth = 0 }) => ({
                    d_depth: d_depth + 1,
                    d_insight_outcome_id: outcomeId,
                }),
                'push',
            );
        },
        [setQueryParams],
    );

    const contextValue = useMemoDeepEqualValue<DialogsContextType>({
        openInsightOutcomeDetails,
        openOrderQuoteDetails,
        openProductDetails,
        openTaskDetails,
    });

    return <DialogsContext.Provider value={contextValue}>{children}</DialogsContext.Provider>;
}

export function AllDialogs(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [queryParams, setQueryParams] = useQueryParams(dialogQueryParams);
    const { isInternal: lastPathIsPlato } = useReferrer();

    const depth = useRef(queryParams.d_depth ?? 0);
    // eslint-disable-next-line react-hooks/refs -- legacy code
    depth.current = queryParams.d_depth ?? 0;

    const shouldDialogOpen = Boolean(
        queryParams.d_product_variant_id ??
            (queryParams.d_transaction_id && queryParams.d_transaction_type) ??
            queryParams.d_task_id ??
            queryParams.d_insight_outcome_id,
    );

    const [isDialogOpen, setDialogOpen] = useState(shouldDialogOpen);

    useEffect(() => {
        setDialogOpen(shouldDialogOpen);
    }, [shouldDialogOpen, queryParams]);

    const closeDialog = useCallback(() => {
        if (lastPathIsPlato) {
            void navigate(-depth.current);
        } else {
            setQueryParams({}, 'replace');
        }
        setDialogOpen(false);
    }, [navigate, setQueryParams, lastPathIsPlato]);

    // eslint-disable-next-line react-hooks/refs -- legacy code
    const goBack = depth.current > 1 ? () => navigate(-1) : undefined;

    return (
        <Dialog open={isDialogOpen} onClose={closeDialog}>
            {queryParams.d_product_variant_id && (
                <DialogContent className="sm:max-w-7xl">
                    {/* Renders as a route page too, so it cannot own the dialog's title. */}
                    <DialogTitle className="sr-only">{t('article', { ns: 'common' })}</DialogTitle>
                    <InventoryDetails
                        customerId={queryParams.d_customer_id}
                        erpProductId={queryParams.d_product_variant_id}
                        onBack={goBack}
                        onClose={closeDialog}
                    />
                </DialogContent>
            )}
            {queryParams.d_transaction_id && queryParams.d_transaction_type && (
                <DialogContent className="sm:max-w-7xl">
                    <DialogTitle className="sr-only">
                        {queryParams.d_transaction_type === SrSalesOrderType.QUOTE
                            ? t('quote', { ns: 'transaction-details' })
                            : t('order', { ns: 'transaction-details' })}
                    </DialogTitle>
                    <QuoteOrderDetailPage
                        onBack={goBack}
                        onClose={closeDialog}
                        transactionId={queryParams.d_transaction_id}
                        type={queryParams.d_transaction_type}
                    />
                </DialogContent>
            )}
            {queryParams.d_task_id && (
                <DialogContent>
                    <TaskDetails taskId={queryParams.d_task_id} onBack={goBack} onClose={closeDialog} />
                </DialogContent>
            )}
            {queryParams.d_insight_outcome_id && (
                <DialogContent>
                    <InsightOutcomeDetails
                        outcomeId={queryParams.d_insight_outcome_id}
                        onBack={goBack}
                        onClose={closeDialog}
                    />
                </DialogContent>
            )}
        </Dialog>
    );
}

export function useDialogs(): DialogsContextType {
    const value = useContext(DialogsContext);
    if (!value) {
        throw new Error(`DialogsContext not found (did you forget to use the AllDialogs component?)`);
    }
    return value;
}
